<template>
  <div
    class="tenant-submission-documents"
  >
    <div
      v-show="files.length > 0"
      class="column-header"
    >
      {{ $t('attachments') }}
    </div>
    <div class="contents">
      <div
        v-for="(file, index) in files"
        :key="index"
        class="file-panel"
      >
        <div class="panel">
          <a
            class="download-link tender-font"
            @click="downloadFile(file.id)"
          >
            {{ file.filename }}
          </a>
          <div v-show="allowChangeName[file.id]">
            <textarea
              v-model="file.description"
              v-autosize
              v-checkVisibility="{refresh: true}"
              class="doc-desc__textarea tender-font"
              autofocus
              @focusout="updateDescription(file)"
            />
          </div>
          <div v-show="!allowChangeName[file.id]">
            <span
              class="desc-text"
              @dblclick="dblClickEdit(file.id)"
            >
              {{ file.description || (canEdit ? $t('project.docs.fillDescHint') : '') }}
            </span>
            <i
              v-if="canEdit"
              class="fa fa-pencil icon"
              @click="Vue.set(allowChangeName, file.id, true)"
            />
          </div>
        </div>
        <div class="panel">
          <i
            v-if="canEdit"
            class="fa fa-times fa-lg"
            @click="deleteFile(file)"
          />
          <p
            v-if="!isTenant"
            class="updator-text"
          >
            {{ $t('tenantProjects.uploadedBy', [findCreatorName(file)]) }}
            <span class="update-date">{{ dateUtil.format(file.createdAt, 'DD MMM YY') }}</span> <span class="update-date">{{ dateUtil.format(file.createdAt, 'HH:mm') }}</span>
          </p>
        </div>
      </div>
      <div
        v-show="canDownloadAll && files.length > 0"
        class="projects-download-all clearfix"
      >
        <button
          class="btn btn--darkblue tender-sticky-button float-left"
          type="button"
          @click="downloadAll(sourceId)"
        >{{ $t('action.download') }} {{ $t('all') }}</button>
        <div class="float-right clearfix">
          <slot name="buttons" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import * as xfs from '@/utils/xfs'
import dateUtil from '@/utils/dateUtil'
import { getAttachments, updateDescription, updateOrder, deleteDocument } from '@/api/docs/request'
import EventBus from '@/utils/eventBus'
import querystring from 'querystring'
import userHelper from '@/utils/userHelper'
import { mapGetters } from 'vuex'

export default {
  name: 'TenantSubmissionDocuments',
  props: {
    canEdit: {
      type: Boolean,
      default () { return false },
    },
    canDownloadAll: {
      type: Boolean,
      default () { return false },
    },
    sourceId: {
      type: String,
      required: true,
    },
    zipName: {
      type: String,
      default: '',
    },
    action: String,
    hiddenAttachments: {
      type: Array,
      default () {
        return []
      },
    },
  },
  data () {
    return {
      Vue,
      dateUtil,
      deep,
      prevSourceId: '',
      allowChangeName: {},
      files: [],
    }
  },

  computed: {
    ...mapGetters('user', ['currentUser', 'isTenant']),
  },

  watch: {
    async sourceId () {
      this.setupListener()
      await this.loadFiles()
    },
    async hiddenAttachments () {
      await this.loadFiles()
    },
    files () {
      this.$emit('update:files', this.files)
    },
  },

  async mounted () {
    this.setupListener()
    await this.loadFiles()
  },

  destroyed () {
    if (this.sourceId) {
      EventBus.$off(`controls.hkldDropzone.newItem.${this.sourceId}`, this.addNewItem)
    }
  },

  methods: {
    async deleteFile (item) {
      const headers = {}
      if (this.action) {
        headers['X-CMS-Action'] = this.action
      }
      const deleted = await deleteDocument(item.id, headers)
      if (deleted) {
        this.files.splice(this.files.indexOf(item), 1)
        EventBus.$emit(`controls.hkldDropzone.deleteItem.${this.sourceId}`, item)
      }
    },

    async downloadFile (fileId) {
      await xfs.downloadFile(`/docs/${fileId}`)
    },

    async downloadAll (sourceId) {
      const filesId = this.files.map(file => file.id)
      const query = querystring.stringify({docsId: filesId})
      await xfs.downloadFile(`/docs/all/${sourceId}?${query}`, this.zipName)
    },

    async updateDescription (item) {
      await updateDescription(item)
      Vue.set(this.allowChangeName, item.id, false)
    },

    async updateAllOrder () {
      await Promise.all(
        this.files.map(async (file, index) => {
          file.order = index
          await updateOrder(file.id, index)
        }),
      )
    },

    formatDate (dateString) {
      return dateUtil.formatDate(dateString, true, true)
    },

    findCreatorName (file) {
      return userHelper.getUserName(deep(file, 'creator.username'), deep(file, 'creator.displayName'))
    },

    async loadFiles () {
      if (!this.sourceId) return
      this.files = await getAttachments(this.sourceId) || []
      if (this.hiddenAttachments.length > 0) {
        const hiddenIds = this.hiddenAttachments.map(item => item.id)
        const result = this.files.filter((item) => hiddenIds.indexOf(item.id) === -1)
        this.files = result || []
      }
    },

    addNewItem (newItem) {
      const isExist = this.files.some(item => item.id === newItem.id)
      if (!isExist) {
        this.files.push(newItem)
      }
    },

    setupListener () {
      if (this.sourceId) {
        EventBus.$off(`controls.hkldDropzone.newItem.${this.prevSourceId}`)
        EventBus.$on(`controls.hkldDropzone.newItem.${this.sourceId}`, this.addNewItem)
        this.prevSourceId = this.sourceId
      }
    },

    dblClickEdit (id) {
      if (this.canEdit) {
        Vue.set(this.allowChangeName, id, true)
      }
    },
  },
}
</script>

<style lang="scss" scoped src="styles/tenants/submission-docs.scss"></style>
<style lang="scss" scoped>
.tenant-submission-documents {
  width: 100%;
  .file-panel {
    .panel {
      padding-left: 0;
    }
    .panel:first-of-type {
      flex: 5;
    }
    .panel:last-of-type {
      flex: 4;
      padding-right: 15px;
      position: relative;
      .fa.fa-times {
        position:absolute;
        right: 0;
        top: 7px;
      }
    }
  }
}
</style>
